import React, { useState } from 'react'
import { Box, IconButton, InputBase, Typography, alpha } from '@mui/material'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

interface PackageSearchProps {
    id: string
    value: string
    placeholder: string
    /** Rotating hint, e.g. "Try Almaty". Empty falls back to the static placeholder. */
    hint?: string
    /** Changes with `hint`; restarts the flip so each hint animates in on its own. */
    hintKey: number
    clearLabel: string
    onChange: (value: string) => void
    onClear: () => void
    isMobile: boolean
}

/**
 * Type-to-filter over the packages on a collection page. It narrows what is already on screen
 * rather than querying anything, so there is no submit button and no spinner — the grid simply
 * follows the field.
 *
 * The hint flips through real destinations until the reader takes the field over, which is what
 * makes an otherwise quiet pill read as something to use. It is painted over the input rather than
 * set as its placeholder, because a placeholder cannot animate.
 */
const PackageSearch: React.FC<PackageSearchProps> = ({
    id,
    value,
    placeholder,
    hint,
    hintKey,
    clearLabel,
    onChange,
    onClear,
    isMobile
}) => {
    const [focused, setFocused] = useState(false)
    const showHint = Boolean(hint) && !focused && !value

    return (
        <Box
            sx={theme => ({
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                /*
                 * Grows into whatever the heading leaves rather than sitting at a fixed 260px:
                 * on a 1280 window that puts its left edge level with the region cards behind
                 * it, and the cap stops it swallowing the whole row on a wide monitor.
                 */
                ...(isMobile
                    ? { width: '100%' }
                    : { flexGrow: 1, flexBasis: 320, minWidth: 260, maxWidth: 460 }),
                px: 1.5,
                py: 0.75,
                // The same pill as the sort control; the brand border and icon are what mark it
                // out as the one thing on the row asking to be used.
                border: 1,
                borderColor: focused || value ? 'primary.main' : alpha(theme.palette.primary.main, 0.4),
                borderRadius: 999,
                bgcolor: 'neutral.white',
                transition: 'border-color 0.2s',
                '&:hover': { borderColor: 'primary.main' },
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
            })}
        >
            <SearchRoundedIcon sx={{ fontSize: 20, color: 'primary.main' }} />

            <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
                <InputBase
                    id={id}
                    type="search"
                    value={value}
                    placeholder={showHint ? '' : placeholder}
                    onChange={event => onChange(event.target.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    sx={{ width: '100%', fontSize: '0.875rem' }}
                    inputProps={{ 'aria-label': placeholder }}
                />

                {showHint && (
                    <Typography
                        key={hintKey}
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            insetInlineStart: 0,
                            top: 0,
                            width: '100%',
                            lineHeight: '23px',
                            fontSize: '0.875rem',
                            color: 'textColors.subheading',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            pointerEvents: 'none',
                            animation: 'holidaySearchHintIn 400ms ease-out',
                            '@keyframes holidaySearchHintIn': {
                                from: { opacity: 0, transform: 'translateY(8px)' },
                                to: { opacity: 1, transform: 'translateY(0)' }
                            },
                            '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
                        }}
                    >
                        {hint}
                    </Typography>
                )}
            </Box>

            {value && (
                <IconButton aria-label={clearLabel} onClick={onClear} size="small" sx={{ p: 0.25 }}>
                    <CloseRoundedIcon sx={{ fontSize: 18, color: 'textColors.subheading' }} />
                </IconButton>
            )}
        </Box>
    )
}

export { PackageSearch }
export default PackageSearch
